import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useI18n } from '../../i18n/I18nProvider';
import { createJsonFetch } from '../accounts/accounts.api';
import { useRouter } from '../../app/router';
import { NotificationsPanel } from './NotificationsPanel';
import {
  loadNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  targetPath,
  unreadCount,
} from './notifications.api';
import type { NotificationKind, NotificationRecord } from './notifications.api';

function useNotifications(): {
  records: NotificationRecord[];
  unread: number;
  error: string | null;
  reload(): void;
  markRead(id: string): void;
  markAllRead(): void;
} {
  const { authorizedFetch } = useAuth();
  const fetchJson = useMemo(() => createJsonFetch(authorizedFetch), [authorizedFetch]);
  const [records, setRecords] = useState<NotificationRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    loadNotifications(fetchJson)
      .then((next) => {
        if (!cancelled) {
          setRecords(next);
          setError(null);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setRecords([]);
          setError(cause instanceof Error ? cause.message : String(cause));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fetchJson, nonce]);

  const reload = useCallback((): void => setNonce((value) => value + 1), []);

  const markRead = useCallback(
    (id: string): void => {
      const stamp = new Date().toISOString();
      setRecords((current) =>
        current.map((record) => (record.id === id ? { ...record, readAt: stamp } : record)),
      );
      markNotificationRead(fetchJson, id).catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : String(cause));
      });
    },
    [fetchJson],
  );

  const markAllRead = useCallback((): void => {
    const stamp = new Date().toISOString();
    setRecords((current) => current.map((record) => ({ ...record, readAt: record.readAt ?? stamp })));
    markAllNotificationsRead(fetchJson).catch((cause: unknown) => {
      setError(cause instanceof Error ? cause.message : String(cause));
    });
  }, [fetchJson]);

  return { records, unread: unreadCount(records), error, reload, markRead, markAllRead };
}

export function NotificationsBell(): JSX.Element {
  const { t } = useI18n();
  const { navigate } = useRouter();
  const { records, unread, error, reload, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const [activeKind, setActiveKind] = useState<NotificationKind | null>(null);

  const toggle = (): void => {
    if (!open) {
      reload();
    }
    setOpen((value) => !value);
  };

  const openRecord = (record: NotificationRecord): void => {
    markRead(record.id);
    const target = targetPath(record);
    if (target !== null) {
      setOpen(false);
      navigate(target);
    }
  };

  return (
    <div className="notifications-bell">
      <button
        type="button"
        className="btn btn--ghost notifications-bell__button"
        data-testid="notifications-bell"
        aria-label={t('notificationsBell')}
        aria-expanded={open}
        onClick={toggle}
      >
        {t('notificationsBell')}
        {unread === 0 ? null : (
          <span className="notifications-bell__count" data-testid="notifications-bell-count">
            {unread}
          </span>
        )}
      </button>
      {open ? (
        <>
          {error === null ? null : (
            <p className="notifications-bell__error" role="alert">
              {`${t('notificationsError')}: ${error}`}
            </p>
          )}
          <NotificationsPanel
            records={records}
            activeKind={activeKind}
            onSelectKind={setActiveKind}
            onNavigate={openRecord}
            onMarkRead={markRead}
            onMarkAllRead={markAllRead}
            onClose={() => setOpen(false)}
          />
        </>
      ) : null}
    </div>
  );
}

/** The full-page route: the same panel, always open. */
export function NotificationsScreen(): JSX.Element {
  const { navigate } = useRouter();
  const { records, error, markRead, markAllRead } = useNotifications();
  const [activeKind, setActiveKind] = useState<NotificationKind | null>(null);

  const openRecord = (record: NotificationRecord): void => {
    markRead(record.id);
    const target = targetPath(record);
    if (target !== null) {
      navigate(target);
    }
  };

  return (
    <section className="notifications-page">
      {error === null ? null : (
        <p className="notifications-bell__error" role="alert">
          {error}
        </p>
      )}
      <NotificationsPanel
        records={records}
        activeKind={activeKind}
        onSelectKind={setActiveKind}
        onNavigate={openRecord}
        onMarkRead={markRead}
        onMarkAllRead={markAllRead}
        onClose={() => navigate('/')}
      />
    </section>
  );
}
